import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from './AuthContext';
import { VCI_EVENTS } from '../constants/telemetry';

const TelemetryContext = createContext();

export const useTelemetry = () => useContext(TelemetryContext);

export const TelemetryProvider = ({ children }) => {
    const { authUser, userProfile, setToastMessage } = useAuth() || {};
    
    const [telemetryState, setTelemetryState] = useState({
        vciScore: 0,
        readinessTier: 'Tier 1: Baseline',
        verifiedTraits: []
    });
    
    const [optimisticVciOffset, setOptimisticVciOffset] = useState(0);
    const [isTransmitting, setIsTransmitting] = useState(false);
    const [currentDifficultyTier, setCurrentDifficultyTier] = useState('modern');

    useEffect(() => {
        if (!authUser) return;
        
        const unsub = onSnapshot(doc(db, 'users', authUser.uid), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setTelemetryState({
                    vciScore: data.vciScore || 0,
                    readinessTier: data.readinessTier || 'Tier 1: Baseline',
                    verifiedTraits: data.verifiedTraits || []
                });
                setOptimisticVciOffset(0);
            }
        });
        
        return () => unsub();
    }, [authUser]);

    const transmitTelemetry = useCallback(async (payload, bypassLock = false) => {
        if (!authUser) return null;

        if (isTransmitting && !bypassLock) {
            console.warn(`[Routing Intercept] Telemetry transmission blocked. A payload is currently in flight.`);
            return { status: 'intercepted', message: 'Transmission in progress' };
        }

        setIsTransmitting(true);

        const nonce = typeof crypto !== 'undefined' && crypto.randomUUID 
            ? crypto.randomUUID() 
            : Date.now().toString() + Math.random().toString(36).substring(2);

        let estimatedVci = VCI_EVENTS[payload.moduleId] || 0;
        
        if (estimatedVci === 0 && Array.isArray(payload.rawInteractions)) {
            estimatedVci += payload.rawInteractions.filter(i => i.status === 'success').length * 15;
        }
        if (estimatedVci === 0) estimatedVci = 50; 

        setOptimisticVciOffset(prev => prev + estimatedVci);

        const securePayload = {
            ...payload,
            nonce,
            clientTimestamp: Date.now(),
            // --- O(1) DENORMALIZATION PATCH ---
            enterpriseTenantId: userProfile?.enterpriseTenantId || 'public'
        };

        try {
            const calculateTelemetry = httpsCallable(functions, 'calculateTelemetry');
            const result = await calculateTelemetry(securePayload);
            
            const awarded = result.data?.awardedVci || result.data?.awardedCxp;
            
            if (awarded > 0 && typeof setToastMessage === 'function') {
                setToastMessage(`Secure Telemetry Logged: +${awarded} VCI`);
            }
            
            return result;
        } catch (e) {
            console.error("[Telemetry Transport Error]:", e);
            if (typeof setToastMessage === 'function') {
                setToastMessage("Warning: Telemetry transmission failed.");
            }
            throw e;
        } finally {
            setOptimisticVciOffset(prev => Math.max(0, prev - estimatedVci));
            setIsTransmitting(false);
        }
    }, [authUser, userProfile, setToastMessage, isTransmitting]);

    const institutionalReadinessScore = useMemo(() => {
        if (!userProfile?.completedModules) return 0;
        const modules = Object.values(userProfile.completedModules);
        if (modules.length === 0) return 0;

        const avgBestScore = modules.reduce((acc, m) => acc + (m.bestScore || 0), 0) / modules.length;
        
        const totalDelta = modules.reduce((acc, m) => acc + (m.confidenceDelta || 0), 0);
        const confidenceScore = Math.min(100, (totalDelta / (modules.length * 9)) * 100);

        const persistenceBonus = Math.min(10, (userProfile.resilience_profile?.persistence_score || 0) * 2);

        const aggs = userProfile.telemetry_aggregates || {};
        const executiveBonus = (aggs.sjtMethodicalCount || 0) * 5;
        const impulsivePenalty = (aggs.sjtActionBiasedCount || 0) * 5;

        const netExecutiveFunction = Math.max(-15, Math.min(15, executiveBonus - impulsivePenalty));
        
        const baseScore = (avgBestScore * 0.6) + (confidenceScore * 0.4);

        return Math.min(100, Math.max(0, Math.round(baseScore + persistenceBonus + netExecutiveFunction)));
    }, [userProfile]);

    const logResilienceFailure = useCallback(async (metrics) => {
        if (!authUser) return null;
        try {
            return {
                type: 'abandonment_threshold',
                tier: currentDifficultyTier,
                jiggleCount: metrics.jiggleMetric ? 1 : 0, 
                recoveryLatencyMs: metrics.recoveryLatency || null,
                timestamp: Date.now(),
                // --- O(1) DENORMALIZATION PATCH ---
                enterpriseTenantId: userProfile?.enterpriseTenantId || 'public'
            };
        } catch (e) { 
            console.error("Failed to package resilience metrics:", e); 
            return null;
        }
    }, [authUser, userProfile, currentDifficultyTier]);

    const logActivity = useCallback(async (type, data) => {
        if (!authUser) return null;
        try {
            return {
                interactionType: type,
                isSurvivalZone: data.isSurvivalZone || false,
                isStabilized: data.isStabilized || false,
                rentToIncomeRatio: data.rentToIncomeRatio || 0,
                timestamp: Date.now(),
                // --- O(1) DENORMALIZATION PATCH ---
                enterpriseTenantId: userProfile?.enterpriseTenantId || 'public'
            };
        } catch(e) { 
            console.error("Activity Sync Failed", e); 
            return null;
        }
    }, [authUser, userProfile]);

    const getThoughtSignature = useCallback(() => {
        if (!userProfile) return null;
        
        const aggs = userProfile.telemetry_aggregates || {};
        const methodical = aggs.sjtMethodicalCount || 0;
        const actionBiased = aggs.sjtActionBiasedCount || 0;
        
        let pacing = "Balanced";
        if (methodical > actionBiased) pacing = "Methodical";
        else if (actionBiased > methodical) pacing = "Action-Biased";

        return {
            adaptabilityScore: userProfile.psychometrics?.currentResilience || 50,
            pacing: pacing
        };
    }, [userProfile]);

    return (
        <TelemetryContext.Provider value={{
            ...telemetryState,
            vciScore: telemetryState.vciScore + optimisticVciOffset,
            transmitTelemetry,
            currentDifficultyTier, setCurrentDifficultyTier,
            institutionalReadinessScore, logResilienceFailure, logActivity, getThoughtSignature
        }}>
            {children}
        </TelemetryContext.Provider>
    );
};